import { useMutation } from '@apollo/client';
import { useArchiveHouseholdCopy } from '@containers/HouseholdModelCopy/useArchiveHouseholdCopy';
import { datadogLogs } from '@datadog/browser-logs';
import { HouseholdPortfolioChangeReasons } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioChange';
import { useHouseholdPortfolioCoordination } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioCoordinationContext';
import { buildDetachClientGroupPortfolioInput } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutationInput';
import { UPDATE_CLIENT_GROUP_PORTFOLIO } from '@ovComponents/4-module/widgets/portfolio/householdPortfolioMutations';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useToast } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DetachModelPortfolioOptions,
  DetachModelPortfolioVisual,
} from './detachModelPortfolio.visual';

export type DetachModelPortfolioProps = {
  options: DetachModelPortfolioOptions;
  /**
   * Household ClientGroup id (the workflow completion `objectId`). Falls back
   * to `workflowData.currentHouseholdClientGroupId`, matching initialize.
   */
  clientGroupId?: string;
  onNext: () => void;
  stepLoading?: boolean;
};

/**
 * Clears the household's model portfolio by attaching a null financial product,
 * retires the copy it was holding, then advances. Nothing is written to the
 * workflow context: detaching is terminal for this branch, and re-running the
 * step on a household that is already detached is harmless.
 */
export const DetachModelPortfolio = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: DetachModelPortfolioProps) => {
  const { workflowData } = useContext(WorkflowContext);
  const { enqueueToast } = useToast();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { notifyChange } = useHouseholdPortfolioCoordination();
  const { t } = useTranslation('workflowCompletions');

  const [updateClientGroupPortfolio, { loading: detachLoading }] = useMutation(
    UPDATE_CLIENT_GROUP_PORTFOLIO,
  );
  const { archiveHouseholdCopy, archiveLoading } = useArchiveHouseholdCopy();

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  const handleDetach = async () => {
    if (!householdClientGroupId) {
      // The workflow is not scoped to a household, so there is nothing to
      // detach from. Distinct copy from a failed detach: retrying won't help.
      enqueueToast(t('householdDetach.missingHousehold'), {
        variant: 'error',
      });
      return;
    }

    try {
      await updateClientGroupPortfolio({
        variables: {
          input: buildDetachClientGroupPortfolioInput(householdClientGroupId),
        },
      });
      // The mutation only returns the client group id, so nothing tells the
      // cache the portfolio is gone. The household widgets watch for this the
      // same way they do when the portfolio page detaches.
      notifyChange(HouseholdPortfolioChangeReasons.detach);
      // Pointer first, archive second, and not the other way around: archiving
      // first would leave the household pointing at an archived model portfolio
      // if the detach then failed, where this order leaves at worst a copy no
      // one holds — which is what a detach leaves behind today, and recoverable.
      await archiveHouseholdCopy(householdClientGroupId, {
        organizationId: workflowData.entityOrganizationId,
      });
      sendAnalytic(ovAnalyticsEvents.householdDetachCommit);
      onNext();
    } catch (error) {
      datadogLogs.logger.error(
        'Household model portfolio detach failed',
        { clientGroupId: householdClientGroupId },
        error instanceof Error ? error : undefined,
      );
      enqueueToast(t('householdDetach.detachError'), { variant: 'error' });
    }
  };

  return (
    <DetachModelPortfolioVisual
      options={options}
      loading={stepLoading || detachLoading || archiveLoading}
      onNext={handleDetach}
    />
  );
};

export default DetachModelPortfolio;
